// OIDC redirect-back handler. react-oidc-context's onSigninCallback strips
// the ?code/?state params; once the AuthProvider finishes processing, we
// restore the requested internal route.
//
// Faz 8.6 / Batch A10 — the bare healthz chrome is replaced with the novem
// brand language (NovemHR-ekranlar login.jsx editorial panel, distilled to a
// centered card): the Novem HR wordmark + a spinner + an italic serif lead while
// the session is verified. The mockup's full split-panel LOGIN form is NOT
// ported — production sign-in is an OIDC redirect (+ in-app dev login), so
// there is no production password screen to restyle (recorded in the port
// notes). The AUTH FLOW below is untouched — only the visual layer changed.

import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";

import { getReturnTo } from "@/auth/return-to";

import { ErrorState, Spinner } from "@novemai/components";

export function CallbackPage(): React.ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect((): void => {
    if (!auth.isLoading && auth.isAuthenticated && !auth.error) {
      void navigate(getReturnTo(auth.user?.state), { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, auth.user?.state, navigate]);

  // Safety net — if a user lands on /callback without OIDC ?code/?state
  // (bookmark, stale link, manual URL), react-oidc-context never fires
  // signinCallback and isLoading hangs true. Bounce home after 5s so the
  // user isn't stuck on "Completing sign-in…" forever.
  useEffect((): (() => void) => {
    const timer = setTimeout((): void => {
      if (!auth.isAuthenticated && !auth.error) {
        void navigate("/", { replace: true });
      }
    }, 5_000);
    return (): void => clearTimeout(timer);
  }, [auth.isAuthenticated, auth.error, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg-surface)] p-6">
      <div className="w-full max-w-[400px] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-9 text-center shadow-[var(--shadow-xs)]">
        {/* Novem HR wordmark — halves from the bundle (identical across locales)
            so the no-literal lint stays authoritative (LeftRail pattern). */}
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <div
            aria-hidden="true"
            className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-[var(--accent)] font-sans text-[15px] font-bold text-[var(--fg-on-accent)]"
          >
            {t("app.wordmark_base").slice(0, 1)}
          </div>
          <div className="font-sans text-[20px] font-semibold tracking-[-0.02em] text-[var(--fg-primary)]">
            {t("app.wordmark_base")}{" "}
            <span className="text-[var(--accent)]">{t("app.wordmark_accent")}</span>
          </div>
        </div>

        {auth.error ? (
          <ErrorState
            title={t("auth.callback_error_title")}
            description={t("auth.callback_error")}
          />
        ) : (
          <div className="flex flex-col items-center gap-4">
            <Spinner aria-label={t("auth.callback_processing")} />
            <div>
              <p className="font-serif text-[17px] text-[var(--fg-primary)]">
                {t("auth.callback_processing")}
              </p>
              <p className="mt-1.5 font-serif text-[13px] italic text-[var(--fg-muted)]">
                {t("auth.callback_subtitle")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
